import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/client'
import Boton from '../components/Boton'
import BotonesCobroWA from '../components/BotonesCobroWA'
import TomarCarrito from '../components/TomarCarrito'
import { useAuth } from '../context/AuthContext'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { fmtFecha, fmtHoraDe, fmtMonto } from '../utils/fecha'
import { colors, font, text, space, radius, sx } from '../design/tokens'

const METODO = { mobbex: 'Tarjeta (Mobbex)', mercadopago: 'MercadoPago', transferencia: 'Transferencia', convenir: 'A convenir' }

/**
 * VENTAS → Carritos abandonados: TODOS los pedidos web sin pagar y qué está
 * haciendo el sistema con cada uno. El circuito vigente (8–10 sep 2026) está
 * descrito en la leyenda de la pantalla y en RecuperoPedidosWebService:
 * escalera 4+3+2 hasta 72 h + 48 h de silencio + 2 avisos finales (10–18),
 * pausa nocturna 23–6, frenos del vendedor (⏸ Frenar con motivo, 🤖 Dejar al
 * sistema), archivo automático al cumplirse (salvo membresías) y
 * recuperación tardía con desarchivo + mail a ventas@.
 */
export default function CarritosAbandonadosPage() {
  const [data, setData] = useState({ data: [], total_plata: 0, en_curso: 0, detenidos: 0, automata_activo: true, etapas: [], resultados: null })
  const [loading, setLoading] = useState(true)
  const [copiado, setCopiado] = useState(null)
  const [accionando, setAccionando] = useState(null)
  const [verRecuperados, setVerRecuperados] = useState(false)
  const navigate = useNavigate()
  const { isSuperAdmin, impersonateRole } = useAuth()
  // Cobro por WhatsApp (Martin, 26 sep 2026): en principio lo administra solo él.
  // La pantalla de plantillas es solo del super admin; los botones WA los decide el backend (administradores).
  const puedeWA = isSuperAdmin && !impersonateRole
  const [errorWA, setErrorWA] = useState(null)

  const cargar = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true)
    try {
      const { data } = await api.get('/carritos-abandonados')
      setData(data)
    } catch { /* transitorio: el próximo refresh lo trae */ } finally { setLoading(false) }
  }, [])

  useEffect(() => { cargar() }, [cargar])
  // Silencioso: sin "Cargando..." ni redibujar la página cada 30 s (Martin, 26 sep 2026).
  useAutoRefresh(() => cargar({ silent: true }), 30000)

  const copiarLink = async (p) => {
    try {
      await navigator.clipboard.writeText(p.url_pago)
      setCopiado(p.id)
      setTimeout(() => setCopiado(null), 1500)
    } catch { /* clipboard denegado: nada que hacer */ }
  }

  const waHref = (p) => {
    const tel = (p.telefono || '').replace(/\D/g, '')
    if (!tel) return null
    const telWa = tel.startsWith('54') ? tel : `54${tel}`
    const texto = `¡Hola ${p.nombre.split(' ')[0]}! Te escribo de Vinoteca Ligier. Vimos que tu pedido ${p.numero} quedó sin completar el pago — ¿te ayudamos a terminarlo? Podés retomarlo acá: ${p.url_pago} — y si preferís otro medio de pago, contame por acá.`
    return `https://wa.me/${telWa}?text=${encodeURIComponent(texto)}`
  }

  const horas = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 3600000)

  const accion = async (p, verbo, body = {}) => {
    setAccionando(p.id)
    try { await api.post(`/carritos-abandonados/${p.id}/${verbo}`, body); await cargar({ silent: true }) }
    catch { /* el refresh lo aclara */ } finally { setAccionando(null) }
  }
  // ⏸ Frenar ES un descarte y se declara por qué (Martin, 8 sep 2026):
  // duplicado · fraude (cliente a lista negra) · fraude solo IP (compra a
  // nombre de otra persona: se corta la IP, el cliente queda limpio — 11 oct 2026).
  const [menuFrenar, setMenuFrenar] = useState(null)   // id del pedido con el menú abierto
  const MOTIVOS_FRENAR = [
    ['duplicado', '📄 Duplicado'],
    ['fraude_ip', '🌐 Fraude — bloquear solo la IP'],
    ['fraude', '🚫 Fraude — a lista negra'],
  ]

  // CARRITOS reales (pre-checkout), capturados por el latido del storefront
  // desde el 23/08: productos en el carrito SIN pedido creado. Antes eran
  // invisibles (viven en el navegador); solo la campaña de Ads los perseguía,
  // por cookie y a ciegas.
  const [carritos, setCarritos] = useState(null)
  useEffect(() => {
    api.get('/carritos-web').then(({ data: d }) => setCarritos(d)).catch(() => {})
  }, [])
  useAutoRefresh(() => api.get('/carritos-web').then(({ data: d }) => setCarritos(d)).catch(() => {}), 60000)

  if (loading) return <p style={{ color: colors.textSec }}>Cargando...</p>

  const items = data.data || []
  const res = data.resultados

  return (
    <div>
      <div style={st.header}>
        <div>
          <h2 style={st.title}>Carritos abandonados</h2>
          <p style={st.sub}>
            Pedidos web sin pagar (últimos 7 días) · {items.length} carrito{items.length !== 1 ? 's' : ''} · ${fmtMonto(data.total_plata)} · {data.en_curso} en persecución · {data.detenidos} detenido{data.detenidos !== 1 ? 's' : ''}
          </p>
        </div>
        <div style={st.headerDer}>
          <span style={{ ...st.automata, ...(data.automata_activo ? st.automataOn : st.automataOff) }}>
            {data.automata_activo ? `🤖 Persecución automática — ${(data.etapas || []).join(' · ')} · pausa 23–6 · avisos finales de 10 a 18 · se corta al archivar la NV` : '⚠ Persecución APAGADA (RECUPERO_PEDIDOS_WEB)'}
          </span>
          {/* Cobro automático por WhatsApp (Martin, 2 oct 2026): mismo lenguaje que el de los mails. */}
          <span style={{ ...st.automata, ...(data.whatsapp?.automatico ? st.automataOn : st.automataOff) }}>
            {data.whatsapp?.automatico
              ? '💬 WhatsApp automático — WA 1 a los 15 min (a cualquier hora) · WA 2 a las 24 h · WA 3 a las 48 h (de 10 a 20) · no sigue si pagó o contestó'
              : '⚠ WhatsApp automático APAGADO (WHATSAPP_COBRO_AUTOMATICO)'}
          </span>
          {puedeWA && (
            <Boton variante="secondary" sm title="Los tres mensajes de cobro por WhatsApp y la respuesta automática (solo super administrador)" onClick={() => navigate('/carritos-abandonados/whatsapp')}>
              💬 Mensajes de cobro por WhatsApp
            </Boton>
          )}
        </div>
      </div>

      {/* Primero: el cliente está en el teléfono y dicta el código del carrito
          (Martin, 15 oct 2026). */}
      <TomarCarrito />

      {errorWA && <div style={st.errorBox}>{errorWA} <button type="button" style={st.errorCerrar} onClick={() => setErrorWA(null)}>✕</button></div>}

      {/* Resultados del recupero: la lista no los puede mostrar porque el que
          paga desaparece de ella — acá queda el rastro de cómo viene la cosa. */}
      {res && (
        <div style={st.resultados}>
          <span style={st.resTitulo}>Resultados · últimos {res.dias} días</span>
          <div style={st.resKpis}>
            <div style={st.resKpi}>
              <span style={st.resNum}>{res.perseguidos}</span>
              <span style={st.resLabel}>perseguidos</span>
            </div>
            <div style={{ ...st.resKpi, cursor: res.recuperados > 0 ? 'pointer' : 'default' }}
              onClick={() => res.recuperados > 0 && setVerRecuperados(v => !v)}
              title={res.recuperados > 0 ? 'Ver el detalle de los recuperados' : undefined}>
              <span style={{ ...st.resNum, color: colors.ok }}>{res.recuperados}</span>
              <span style={st.resLabel}>recuperados · ${fmtMonto(res.plata_recuperada)}{res.recuperados > 0 ? (verRecuperados ? ' ▴' : ' ▾') : ''}</span>
            </div>
            <div style={st.resKpi}>
              <span style={st.resNum}>{res.compro_por_otro_lado}</span>
              <span style={st.resLabel}>compró por otro lado</span>
            </div>
            <div style={st.resKpi} title="Pagó este pedido pero el cobro no vino del click del mail: venta común, no recuperación">
              <span style={st.resNum}>{res.pagado_sin_click ?? 0}</span>
              <span style={st.resLabel}>pagó sin click del mail</span>
            </div>
            <div style={st.resKpi}>
              <span style={st.resNum}>{res.en_curso}</span>
              <span style={st.resLabel}>en curso</span>
            </div>
            <div style={st.resKpi}>
              <span style={{ ...st.resNum, color: res.vencidos > 0 ? '#b91c1c' : colors.text }}>{res.vencidos}</span>
              <span style={st.resLabel}>vencieron sin pagar</span>
            </div>
            <div style={st.resKpi}>
              <span style={st.resNum}>{res.mails_enviados}</span>
              <span style={st.resLabel}>mails enviados</span>
            </div>
          </div>
          {verRecuperados && (res.detalle_recuperados || []).length > 0 && (
            <div style={st.resDetalle}>
              {res.detalle_recuperados.map(r => (
                <div key={r.numero} style={st.resFila}>
                  <span style={{ fontFamily: font.mono, fontWeight: 500 }}>{r.numero}</span>
                  <span>{r.nombre || '—'}</span>
                  <span style={{ fontFamily: font.mono, fontWeight: 600 }}>${fmtMonto(r.total)}</span>
                  <span style={{ color: colors.textMeta }}>abandonado {fmtFecha(r.abandonado)} · pagó {fmtFecha(r.pagado)} {fmtHoraDe(r.pagado)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div style={st.empty}>No hay carritos abandonados — todos los pedidos web recientes están pagos. 🎉</div>
      ) : (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                {['Pedido', 'Hace', 'Cliente', 'Contacto', 'Medio', 'Total', 'Qué hace el sistema', ...(data.whatsapp?.puede_enviar ? ['WhatsApp'] : []), ''].map(h => (
                  <th key={h} style={st.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(p => {
                const wa = waHref(p)
                return (
                  <tr key={p.id} style={st.tr}>
                    <td style={st.tdMono}>
                      <div>{p.numero}</div>
                      {/* El pedido SIEMPRE entró a LGR como NV en borrador — link directo. */}
                      {p.nv && (
                        <div
                          style={st.nvLink}
                          title={`Ver la nota de venta ${p.nv.numero}${p.nv.archivada ? ' (archivada — solo oculta del listado de NV)' : ''}`}
                          onClick={() => navigate(`/notas-venta/${p.nv.id}`)}
                        >
                          {p.nv.numero}{p.nv.archivada ? ' · archivada' : ''} ↗
                        </div>
                      )}
                    </td>
                    <td style={st.tdSec} title={`${fmtFecha(p.fecha)} ${fmtHoraDe(p.fecha)}`}>
                      {horas(p.fecha) < 24 ? `${horas(p.fecha)} h` : `${Math.floor(horas(p.fecha) / 24)} d`}
                    </td>
                    <td style={st.td}>
                      <span style={st.principal}>{p.nombre || '—'}</span>
                      {p.ya_compro && (
                        /* Clickeable: abre la NV de la compra concretada — sin
                           esto había que salir a buscarla a mano (y el listado
                           de NV puede tenerla oculta por "Ocultar completas"). */
                        <div
                          style={{ ...st.yaCompro, cursor: p.ya_compro.nv_id ? 'pointer' : 'default' }}
                          title={`${p.ya_compro.detalle} — ${fmtFecha(p.ya_compro.fecha)} ${fmtHoraDe(p.ya_compro.fecha)}. No perseguir: el autómata la saltea solo.${p.ya_compro.nv_id ? ' Click para ver esa venta.' : ''}`}
                          onClick={() => p.ya_compro.nv_id && navigate(`/notas-venta/${p.ya_compro.nv_id}`)}
                        >
                          ✓ Ya compró · {p.ya_compro.referencia} {p.ya_compro.nv_id ? '↗' : ''}
                        </div>
                      )}
                    </td>
                    <td style={st.tdSec}>
                      <div>{p.email}</div>
                      {p.telefono && <div style={{ color: colors.textMeta }}>{p.telefono}</div>}
                    </td>
                    <td style={st.tdSec}>{METODO[p.payment_method] || p.payment_method || '—'}</td>
                    <td style={{ ...st.tdMono, textAlign: 'right', fontWeight: 600 }}>${fmtMonto(p.total)}</td>
                    <td style={st.td}>
                      {/* Qué está haciendo el sistema: estado en palabras + los 11
                          puntos del ciclo (✓ enviado · ○ pendiente · × salteado). */}
                      <div style={{ ...st.cicloTxt, ...(p.ciclo.estado === 'detenido' ? st.cicloDet : p.ciclo.estado === 'terminado' ? st.cicloFin : {}) }}>
                        {p.ciclo.descripcion}
                      </div>
                      <div style={{ display: 'flex', gap: 3, alignItems: 'center', marginTop: 3 }}>
                        {p.ciclo.etapas.map(e => (
                          <span
                            key={e.etapa}
                            title={e.at
                              ? (e.salteada ? `Mail ${e.etapa} (${e.etiqueta}) salteado — el sistema no estuvo a tiempo` : `Mail ${e.etapa} (${e.etiqueta}) enviado ${fmtFecha(e.at)} ${fmtHoraDe(e.at)}`)
                              : `Mail ${e.etapa} — a las ${e.etiqueta} del pedido`}
                            style={{ ...st.toque, ...(e.at ? (e.salteada ? st.toqueSalt : st.toqueOk) : {}) }}
                          >{e.etapa}</span>
                        ))}
                      </div>
                    </td>
                    {/* Cobro por WhatsApp desde la LÍNEA DEL SISTEMA (Martin, 26 sep
                        2026): tres mensajes manuales, plantillas aprobadas por Meta.
                        Columna propia para que WA 1·2·3 y sus tags queden alineados. */}
                    {data.whatsapp?.puede_enviar && (
                      <td style={st.td}>
                        {p.whatsapp && p.whatsapp.telefono_e164 ? (
                          <BotonesCobroWA
                            columnas
                            pedidoId={p.id}
                            whatsapp={p.whatsapp}
                            plantillas={data.whatsapp?.plantillas || null}
                            onActualizado={(w) => setData(d => ({ ...d, data: d.data.map(x => x.id === p.id ? { ...x, whatsapp: w } : x) }))}
                            onError={setErrorWA}
                          />
                        ) : (
                          <span style={{ fontSize: text.meta, color: colors.textMeta }} title="El pedido no tiene un teléfono válido">sin teléfono</span>
                        )}
                      </td>
                    )}
                    <td style={{ ...st.td, whiteSpace: 'nowrap' }}>
                      {p.ciclo.estado === 'en_curso' ? (
                        <span style={{ position: 'relative', display: 'inline-block' }}>
                          <Boton variante="ghost" sm disabled={accionando === p.id} title="Descartar el pedido: corta los mails y archiva la NV. Se declara el motivo (fraude manda el cliente a lista negra)." onClick={() => setMenuFrenar(v => v === p.id ? null : p.id)}>
                            {accionando === p.id ? '…' : '⏸ Frenar'}
                          </Boton>
                          {menuFrenar === p.id && (
                            <div style={st.menuFrenar}>
                              <div style={st.menuFrenarTitulo}>¿Por qué se descarta?</div>
                              {MOTIVOS_FRENAR.map(([motivo, texto]) => (
                                <button key={motivo} type="button" style={{ ...st.menuFrenarOp, ...(motivo === 'fraude' ? { color: '#b91c1c' } : {}) }}
                                  onClick={() => { setMenuFrenar(null); accion(p, 'frenar', { motivo }) }}>
                                  {texto}
                                </button>
                              ))}
                              <div style={st.menuFrenarTitulo}>¿O solo cortar los mails?</div>
                              <button type="button" style={st.menuFrenarOp}
                                onClick={() => { setMenuFrenar(null); accion(p, 'frenar-mails') }}>
                                ✉️ Frenar mails — la NV sigue activa
                              </button>
                              <button type="button" style={st.menuFrenarCancel} onClick={() => setMenuFrenar(null)}>Cancelar</button>
                            </div>
                          )}
                        </span>
                      ) : p.ciclo.estado === 'detenido' ? (
                        <Boton variante="primary" sm disabled={accionando === p.id} title="Desarchiva la NV: el ciclo sigue desde donde marca el reloj" onClick={() => accion(p, 'reanudar')}>
                          {accionando === p.id ? '…' : '▶ Reanudar'}
                        </Boton>
                      ) : null}
                      {wa && (
                        <a href={wa} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', margin: '0 6px' }}>
                          <Boton variante="secondary" sm tabIndex={-1}>📱 WhatsApp</Boton>
                        </a>
                      )}
                      <Boton variante="ghost" sm onClick={() => copiarLink(p)}>
                        {copiado === p.id ? '✓ Copiado' : '🔗 Link'}
                      </Boton>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={st.hint}>
        <div style={st.hintTitulo}>Cómo funciona hoy la persecución (9–10 sep 2026)</div>
        <ol style={st.hintLista}>
          <li><b>Arranca sola</b> con cada pedido web sin pagar. No se persigue a pedidos manuales ni a pagos <b>a convenir</b>; la <b>transferencia</b> sí, hasta que el cliente cargue su comprobante o se facture (verificarla es del vendedor). Todo pedido de acá <b>ya entró a LGR</b> como NV en borrador (link bajo el número).</li>
          <li><b>11 mails</b>, el link retoma el mismo pedido. Escalera: día 1 — <b>15 min · 1 h · 6 h · 12 h</b>; día 2 — <b>24 h · 33 h · 44 h</b>; día 3 — <b>56 h · 71 h</b>. Después <b>48 h de silencio</b> y dos avisos finales el <b>día 5</b> y el <b>día 6</b>, solo entre las <b>10 y las 18</b>. <b>Pausa nocturna</b>: nada sale entre las 23 y las 6 (lo vencido de noche sale a las 6, y nunca en ráfaga).</li>
          <li><b>Frenos del vendedor.</b> <b>⏸ Frenar</b> descarta el pedido declarando el motivo — <b>duplicado</b> o <b>fraude</b> (el cliente va a lista negra) — y archiva la NV; <b>▶ Reanudar</b> desarchiva y el ciclo sigue desde donde marca el reloj. <b>🤖 Dejar al sistema</b> (desde Notas de Venta) lo saca del listado del vendedor <b>sin cortar</b> los mails.</li>
          <li><b>Termina solo</b> cuando el cliente paga, la NV se procesa, la persona compró por otro lado (<b>✓ Ya compró</b>), se hizo socio del Wine Club, cargó el comprobante, está en lista negra, o salió el último aviso. Cumplido el ciclo sin pago, <b>el sistema archiva la NV solo</b> — salvo que el pedido traiga una <b>membresía</b>: esa queda en la lista para que la decida una persona.</li>
          <li><b>Recuperados.</b> Cuando entra un cobro o un comprobante, aunque la NV ya estuviera archivada: se <b>desarchiva sola</b>, queda <b>verde y primera</b> en Notas de Venta con un cartel al abrirla, sale un <b>mail a ventas@ligier.com.ar</b> y el parte matinal la recuerda hasta que se procese. Al facturarse vuelve a su lugar por fecha.</li>
          <li>Un carrito sale de esta lista cuando paga o a los 7 días; su destino queda en el panel de Resultados.</li>
        </ol>
      </div>

      {/* Carritos PRE-CHECKOUT (pedido de Martin 23/08): otra cosa que los
          pedidos sin pagar de arriba — acá todavía no hay orden creada.
          Va al final a pedido de Martin: primero los pedidos, después los
          carritos. */}
      {carritos && (
        <div style={{ background: colors.bg, border: `1px solid ${colors.borderSoft}`, borderRadius: 8, padding: '12px 16px', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: text.body }}>🛒 Carritos (sin orden creada)</strong>
            <span style={{ fontSize: text.sec, color: colors.textSec }}>
              {carritos.resumen.abandonados} abandonados · ${fmtMonto(carritos.resumen.abandonados_total)} ·
              {' '}{carritos.resumen.abandonados_con_email} con email · {carritos.resumen.activos_ahora} navegando ahora ·
              {' '}{carritos.resumen.convertidos} convertidos en pedido
            </span>
            <span style={{ fontSize: text.meta, color: colors.textMeta }}>
              {carritos.resumen.capturando_desde
                ? `capturando desde ${fmtFecha(carritos.resumen.capturando_desde)} — los carritos previos de navegadores que no vuelven no se ven`
                : 'todavía sin datos: se capturan a medida que los clientes navegan'}
            </span>
          </div>
          {/* Cómo conviven los dos circuitos (Martin, 5 sep 2026): los pedidos
              sin pagar de arriba los persigue el vendedor con ▶ Perseguir; los
              carritos con email de acá abajo reciben SOLOS un mail automático
              después de las 18 (3 a 48 h sin moverse), y el cazador no les
              escribe en los 14 días siguientes. */}
          <div style={{ fontSize: text.meta, color: colors.textSec, marginTop: 6 }}>
            Los carritos <strong>con email</strong> reciben solos un recordatorio automático después de las 18 h (entre 3 y 48 h sin moverse, una vez por carrito);
            los anónimos no se pueden escribir. Los pedidos sin pagar de arriba siguen siendo tuyos: ▶ Perseguir.
            La última columna dice qué hizo el automático con cada carrito.
          </div>
          {(carritos.abandonados_detalle || []).length > 0 && (
            <table style={{ width: '100%', marginTop: 8, fontSize: text.sec, borderCollapse: 'collapse' }}>
              <tbody>
                {carritos.abandonados_detalle.slice(0, 15).map(c => (
                  <tr key={c.id} style={{ borderTop: `1px solid ${colors.borderSoft}` }}>
                    <td style={{ padding: '4px 8px 4px 0', whiteSpace: 'nowrap', color: colors.textSec }}>{fmtFecha(c.visto_ultima_vez)} {fmtHoraDe(c.visto_ultima_vez)}</td>
                    <td style={{ padding: '4px 8px', whiteSpace: 'nowrap', ...sx.mono, color: colors.textSec }} title="Código del carrito: el que ve el cliente en la tienda">{c.codigo || '—'}</td>
                    <td style={{ padding: '4px 8px' }}>{c.nombre || c.email || <span style={{ color: colors.textMeta }}>anónimo (solo Ads lo puede perseguir)</span>}</td>
                    <td style={{ padding: '4px 8px', color: colors.textSec, maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(c.lineas || []).map(l => `${l.cantidad}× ${l.nombre || l.sku}`).join(' · ')}
                    </td>
                    <td style={{ padding: '4px 0 4px 8px', textAlign: 'right', fontFamily: font.mono, whiteSpace: 'nowrap' }}>${fmtMonto(c.total)}</td>
                    <td style={{ padding: '4px 0 4px 12px', whiteSpace: 'nowrap', fontSize: text.meta, color: colors.textSec }} title={c.recordatorio?.motivo || ''}>
                      {!c.email ? <span style={{ color: colors.textMeta }}>sin email</span>
                        : !c.recordatorio ? <span style={{ color: colors.textMeta }}>mail pendiente (después de las 18)</span>
                        : c.recordatorio.estado === 'enviado' ? <span style={{ color: colors.ok }}>✉ enviado {fmtFecha(c.recordatorio.enviado_at)} {fmtHoraDe(c.recordatorio.enviado_at)}{c.recordatorio.click ? ' · click' : c.recordatorio.abierto ? ' · abierto' : ''}</span>
                        : c.recordatorio.estado === 'fallido' ? <span style={{ color: colors.errStrong }}>⚠ falló</span>
                        : <span>omitido: {c.recordatorio.motivo}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

    </div>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: space.xl, flexWrap: 'wrap', gap: space.lg },
  headerDer: { display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap', justifyContent: 'flex-end' },
  errorBox: { background: colors.errBg, color: colors.err, borderRadius: radius.sm, padding: '8px 12px', marginBottom: space.lg, fontSize: text.sec, display: 'flex', justifyContent: 'space-between', gap: space.md },
  errorCerrar: { border: 'none', background: 'none', cursor: 'pointer', color: colors.err, fontSize: text.sec },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: '2px 0 0', fontSize: text.sec, color: colors.textSec },
  automata: { fontSize: text.sec, fontWeight: 500, padding: '6px 12px', borderRadius: radius.md, alignSelf: 'center' },
  automataOn: { background: colors.okBg, color: colors.ok, border: '1px solid #86efac' },
  automataOff: { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' },
  cicloTxt: { fontSize: text.sec, color: colors.text, whiteSpace: 'nowrap' },
  menuFrenar: { position: 'absolute', top: '110%', right: 0, zIndex: 30, minWidth: 210, background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 4, textAlign: 'left' },
  menuFrenarTitulo: { padding: '5px 10px', fontSize: '0.7rem', color: colors.textMeta },
  menuFrenarOp: { display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: '7px 10px', borderRadius: radius.sm, cursor: 'pointer', fontSize: text.sec, color: colors.text },
  menuFrenarCancel: { display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: '5px 10px', fontSize: '0.72rem', color: colors.textMeta, cursor: 'pointer' },
  cicloDet: { color: colors.warn, fontWeight: 600 },
  cicloFin: { color: colors.textMeta },
  toqueSalt: { borderStyle: 'dashed', color: colors.textMeta },
  empty: { background: colors.bg, borderRadius: radius.md, padding: 40, textAlign: 'center', color: colors.textMeta, border: `1px solid ${colors.border}` },
  tableWrap: { background: colors.bg, borderRadius: radius.md, border: `1px solid ${colors.border}`, overflow: 'auto', fontVariantNumeric: 'tabular-nums' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: { padding: '8px 14px', textAlign: 'left', background: colors.bgSoft, borderBottom: `1px solid ${colors.border}`, fontWeight: 600, fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  tr: { borderBottom: `1px solid ${colors.borderSoft}`, height: 52 },
  td: { padding: '8px 14px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, lineHeight: 1.3 },
  tdSec: { padding: '8px 14px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, lineHeight: 1.3 },
  tdMono: { padding: '8px 14px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, fontFamily: font.mono, fontWeight: 500, whiteSpace: 'nowrap' },
  principal: { fontSize: text.body, fontWeight: 500, color: colors.text },
  toque: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: '50%', fontSize: 10, fontWeight: 700, background: colors.bgSoft, color: colors.textPlh, border: `1px solid ${colors.border}`, cursor: 'default' },
  toqueOk: { background: colors.okBg, color: colors.ok, border: '1px solid #86efac' },
  nvLink: { fontSize: text.meta, fontFamily: font.mono, color: colors.primary, cursor: 'pointer', marginTop: 2, whiteSpace: 'nowrap' },
  yaCompro: { display: 'inline-block', fontSize: text.meta, fontWeight: 600, color: colors.ok, background: colors.okBg, border: '1px solid #86efac', padding: '1px 8px', borderRadius: radius.sm, marginTop: 3, whiteSpace: 'nowrap', cursor: 'default' },
  resultados: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, marginBottom: space.lg },
  resTitulo: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  resKpis: { display: 'flex', gap: space.xl, flexWrap: 'wrap', marginTop: space.sm },
  resKpi: { display: 'flex', flexDirection: 'column', gap: 1 },
  resNum: { fontSize: 20, fontWeight: 600, color: colors.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 },
  resLabel: { fontSize: text.meta, color: colors.textMeta, whiteSpace: 'nowrap' },
  resDetalle: { marginTop: space.md, borderTop: `1px solid ${colors.borderSoft}`, paddingTop: space.sm, display: 'flex', flexDirection: 'column', gap: 4 },
  resFila: { display: 'flex', gap: space.lg, fontSize: text.sec, color: colors.textSec, alignItems: 'baseline', flexWrap: 'wrap' },
  hint: { marginTop: space.lg, fontSize: text.sec, color: colors.textMeta, lineHeight: 1.5, maxWidth: 760 },
  hintTitulo: { fontWeight: 600, color: colors.text, marginBottom: 6 },
  hintLista: { margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 },
}
